import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, getCategoryColor } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Type,
  AlignLeft,
  Hash,
  Calendar,
  CheckSquare,
  List,
  ToggleLeft,
  Loader2,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { type: 'text', label: 'Text', icon: Type },
  { type: 'textarea', label: 'Long Text', icon: AlignLeft },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'select', label: 'Dropdown', icon: List },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { type: 'radio', label: 'Radio Group', icon: ToggleLeft },
];

const CATEGORIES = [
  { value: 'registration', label: 'Registration' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'progress', label: 'Progress Note' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'lab', label: 'Lab Order' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'discharge', label: 'Discharge' },
];

export default function FormBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'consultation',
    is_active: true,
    fields: [],
  });
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchTemplate();
    }
  }, [id]);

  const fetchTemplate = async () => {
    try {
      const response = await formsAPI.getTemplateById(id);
      setFormData(response.data);
    } catch (err) {
      toast.error('Failed to load template');
      navigate('/forms');
    }
    setLoading(false);
  };

  const addField = (type) => {
    const newField = {
      type,
      label: `New ${type} field`,
      name: `field_${Date.now()}`,
      required: false,
      placeholder: '',
      options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : null,
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    setSelectedFieldIndex(formData.fields.length);
  };

  const updateField = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f)
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
    setSelectedFieldIndex(null);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFields = [...formData.fields];
    const draggedField = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);
    
    setFormData(prev => ({ ...prev, fields: newFields }));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    if (formData.fields.length === 0) {
      toast.error('Please add at least one field');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await formsAPI.updateTemplate(id, formData);
        toast.success('Form template updated');
      } else {
        await formsAPI.createTemplate(formData);
        toast.success('Form template created');
      }
      navigate('/forms');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save form');
    }
    setSaving(false);
  };

  const selectedField = selectedFieldIndex !== null ? formData.fields[selectedFieldIndex] : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col animate-fadeIn" data-testid="form-builder">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/forms')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {isEdit ? 'Edit Form Template' : 'Create Form Template'}
            </h1>
            <p className="text-muted-foreground">
              Design your medical form with drag-and-drop fields
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-form-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Template
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-12 gap-6 min-h-0">
        {/* Left: Field Palette */}
        <div className="col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Form Fields</CardTitle>
              <CardDescription>Drag or click to add</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {FIELD_TYPES.map(({ type, label, icon: Icon }) => (
                  <Button
                    key={type}
                    variant="outline"
                    className="h-auto py-3 flex-col gap-1 hover:border-primary hover:bg-primary/5"
                    onClick={() => addField(type)}
                    data-testid={`add-field-${type}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs">{label}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center: Canvas */}
        <div className="col-span-6">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-3">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="form-name">Form Name *</Label>
                    <Input
                      id="form-name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Patient Consultation"
                      data-testid="form-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="form-category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger data-testid="form-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="form-description">Description</Label>
                  <Input
                    id="form-description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this form"
                    data-testid="form-description-input"
                  />
                </div>
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="flex-1 p-4 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="form-canvas p-4 rounded-lg min-h-[300px]">
                  {formData.fields.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Plus className="h-12 w-12 mx-auto mb-4 opacity-20" />
                      <p>Click a field type to add it here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.fields.map((field, index) => (
                        <div
                          key={field.name}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          onClick={() => setSelectedFieldIndex(index)}
                          className={cn(
                            'flex items-center gap-3 p-3 bg-card rounded-lg border cursor-pointer transition-all',
                            selectedFieldIndex === index && 'border-primary ring-2 ring-primary/20',
                            draggedIndex === index && 'opacity-50'
                          )}
                          data-testid={`field-${index}`}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{field.label}</span>
                              {field.required && (
                                <Badge variant="destructive" className="text-[10px] px-1">Required</Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground capitalize">{field.type}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeField(index);
                            }}
                            data-testid={`remove-field-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right: Properties Panel */}
        <div className="col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                Properties
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedField ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Label</Label>
                    <Input
                      value={selectedField.label}
                      onChange={(e) => updateField(selectedFieldIndex, { label: e.target.value })}
                      data-testid="field-label-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Field Name</Label>
                    <Input
                      value={selectedField.name}
                      onChange={(e) => updateField(selectedFieldIndex, { 
                        name: e.target.value.replace(/\s+/g, '_').toLowerCase() 
                      })}
                      data-testid="field-name-input"
                    />
                  </div>

                  {(selectedField.type === 'text' || selectedField.type === 'textarea' || selectedField.type === 'number') && (
                    <div className="space-y-2">
                      <Label>Placeholder</Label>
                      <Input
                        value={selectedField.placeholder || ''}
                        onChange={(e) => updateField(selectedFieldIndex, { placeholder: e.target.value })}
                        data-testid="field-placeholder-input"
                      />
                    </div>
                  )}

                  {(selectedField.type === 'select' || selectedField.type === 'radio') && (
                    <div className="space-y-2">
                      <Label>Options (one per line)</Label>
                      <Textarea
                        value={selectedField.options?.join('\n') || ''}
                        onChange={(e) => updateField(selectedFieldIndex, { 
                          options: e.target.value.split('\n').filter(Boolean) 
                        })}
                        rows={4}
                        data-testid="field-options-input"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <Label htmlFor="required-toggle">Required</Label>
                    <Switch
                      id="required-toggle"
                      checked={selectedField.required}
                      onCheckedChange={(checked) => updateField(selectedFieldIndex, { required: checked })}
                      data-testid="field-required-toggle"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings2 className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Select a field to edit its properties</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
